import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useSpeechToText — thin wrapper around the browser-native Web Speech API
 * (SpeechRecognition / webkitSpeechRecognition). Zero cost, no external
 * service. Mirrors the inline dictation logic used in the Journal panel so
 * the two behave identically.
 *
 * Usage:
 *   const { supported, isListening, interimTranscript, toggle, stop } =
 *     useSpeechToText({ onFinalText: (text) => appendToInput(text) });
 *
 * - supported:          false when the browser lacks SpeechRecognition.
 * - isListening:        true while actively dictating.
 * - interimTranscript:  the in-progress (not yet final) phrase, for a live
 *                       "listening..." preview. Cleared on final/stop.
 * - onFinalText:        called with each finalized chunk of recognized text.
 *
 * The recognizer is stopped automatically on unmount.
 */
export default function useSpeechToText({ onFinalText, lang = "en-US" } = {}) {
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef(null);
  const onFinalTextRef = useRef(onFinalText);

  // Keep the latest callback without re-creating the recognizer.
  useEffect(() => { onFinalTextRef.current = onFinalText; }, [onFinalText]);

  const supported =
    typeof window !== "undefined" &&
    (typeof window.SpeechRecognition !== "undefined" ||
      typeof window.webkitSpeechRecognition !== "undefined");

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore — recognizer may already be stopped
    }
    recognitionRef.current = null;
    setIsListening(false);
    setInterimTranscript("");
  }, []);

  const start = useCallback(() => {
    if (!supported || recognitionRef.current) return;
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) final += result[0].transcript;
        else interim += result[0].transcript;
      }
      if (final.trim()) {
        onFinalTextRef.current?.(final.trim());
        setInterimTranscript("");
      } else {
        setInterimTranscript(interim);
      }
    };
    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event?.error || event);
      recognitionRef.current = null;
      setIsListening(false);
      setInterimTranscript("");
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setIsListening(false);
      setInterimTranscript("");
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
    } catch (err) {
      console.error("Failed to start speech recognition:", err);
      recognitionRef.current = null;
      setIsListening(false);
      setInterimTranscript("");
    }
  }, [supported, lang]);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  // Stop dictation when the consuming component unmounts.
  useEffect(() => () => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore
    }
    recognitionRef.current = null;
  }, []);

  return { supported, isListening, interimTranscript, start, stop, toggle };
}
